import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui'
import { useAuth } from '@/contexts/auth'
import {
  inviteUrl,
  useActiveClubInvite,
  useCreateClubInvite,
  useRevokeClubInvite,
} from '@/hooks/clubs'

/** Generate, copy/share and revoke the club's invite link */
export function ClubInviteCard({
  clubId,
  clubName,
  isAdmin,
}: {
  clubId: string
  clubName: string
  isAdmin: boolean
}) {
  const { t, i18n } = useTranslation()
  const { user } = useAuth()
  const { data: invite, isLoading } = useActiveClubInvite(clubId)
  const createInvite = useCreateClubInvite()
  const revokeInvite = useRevokeClubInvite()
  const [copied, setCopied] = useState(false)

  const url = invite ? inviteUrl(invite.token) : null
  const canRevoke = !!invite && (isAdmin || invite.created_by === user?.id)

  const share = async () => {
    if (!url) return
    const text = t('clubs.invite.shareText', { club: clubName })
    try {
      if (navigator.share) {
        await navigator.share({ title: clubName, text, url })
        return
      }
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Share sheet closed or clipboard blocked
    }
  }

  return (
    <Card className="border-0 shadow-sm min-w-0">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold">✉️ {t('clubs.invite.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="h-10 animate-pulse rounded bg-muted" />
        ) : url && invite ? (
          <>
            <div className="truncate rounded-md border border-border bg-muted/40 px-3 py-2 font-mono text-xs text-foreground">
              {url}
            </div>
            <p className="text-xs text-muted-foreground">
              {t('clubs.invite.expires', {
                date: new Intl.DateTimeFormat(i18n.language, {
                  day: 'numeric',
                  month: 'short',
                }).format(new Date(invite.expires_at)),
              })}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={share}>
                {copied ? t('clubs.invite.copied') : t('clubs.invite.share')}
              </Button>
              {canRevoke && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={revokeInvite.isPending}
                  onClick={() => revokeInvite.mutate(invite)}
                >
                  {t('clubs.invite.revoke')}
                </Button>
              )}
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">{t('clubs.invite.description')}</p>
            <Button
              size="sm"
              disabled={createInvite.isPending}
              onClick={() => createInvite.mutate(clubId)}
            >
              {t('clubs.invite.create')}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  )
}
